import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../auth/AuthProvider';

interface Permission {
  resource: string;
  actions: string[];
}

interface RBACContextType {
  roles: string[];
  permissions: Permission[];
  loading: boolean;
  hasRole: (role: string) => boolean;
  hasPermission: (resource: string, action: string) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isEditor: () => boolean;
  canAccessWorkflowStudio: () => boolean;
  refreshPermissions: () => Promise<void>;
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

interface RBACProviderProps {
  children: ReactNode;
}

export const RBACProvider: React.FC<RBACProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);

  const loadPermissions = async () => {
    if (!user) {
      setRoles([]);
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.get('/api/rbac/me');
      setRoles(response.roles || ['user']);
      setPermissions(response.permissions || [
        { resource: 'workflows', actions: ['read', 'execute'] },
        { resource: 'jobs', actions: ['create', 'read', 'update'] },
        { resource: 'files', actions: ['create', 'read', 'update', 'delete'] },
        { resource: 'chat', actions: ['create', 'read'] }
      ]);
    } catch (error) {
      console.error('Failed to load user permissions:', error);
      // SECURITY: Graceful fallback to minimal user permissions (NOT admin)
      console.warn('RBAC endpoint failed, using default user permissions');
      setRoles(['user']);
      setPermissions([
        { resource: 'workflows', actions: ['read', 'execute'] },
        { resource: 'jobs', actions: ['create', 'read', 'update'] },
        { resource: 'files', actions: ['create', 'read', 'update', 'delete'] },
        { resource: 'chat', actions: ['create', 'read'] }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, [user]);

  const hasRole = (role: string): boolean => {
    return roles.includes(role);
  };

  const hasPermission = (resource: string, action: string): boolean => {
    return permissions.some(permission => 
      (permission.resource === '*' || permission.resource === resource) &&
      (permission.actions.includes('*') || permission.actions.includes(action))
    );
  };

  const isAdmin = (): boolean => {
    return hasRole('admin') || hasRole('super-admin');
  };

  const isSuperAdmin = (): boolean => {
    return hasRole('super-admin');
  };

  const isEditor = (): boolean => {
    return hasRole('editor') || isAdmin();
  };

  const canAccessWorkflowStudio = (): boolean => {
    return isEditor();
  };

  const refreshPermissions = async (): Promise<void> => {
    await loadPermissions();
  };

  const value: RBACContextType = {
    roles,
    permissions,
    loading,
    hasRole,
    hasPermission,
    isAdmin,
    isSuperAdmin,
    isEditor,
    canAccessWorkflowStudio,
    refreshPermissions
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = (): RBACContextType => {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
};